import InvoiceCalculator from '../js/services/invoiceCalculator';
import moment from 'moment';

// Helper function to get applicable discount for a given invoice number
function getApplicableDiscount(discountInfo, invoiceNumber) {
  if (!discountInfo || !discountInfo.discounts || discountInfo.discounts.length === 0) {
    return null;
  }

  // If "apply to all" is true, use the first discount for all invoices
  if (discountInfo.applyToAll) {
    return discountInfo.discounts[0];
  }

  // Otherwise, find the discount for this specific invoice number (1-indexed)
  // discounts[0] = invoice 1, discounts[1] = invoice 2, etc.
  const index = invoiceNumber - 1;
  return discountInfo.discounts[index] || null;
}

// Helper function to apply discount to a price
function applyDiscount(price, amount, discount) {
  if (!discount || !discount.value) {
    return { finalPrice: price, discountAmount: 0 };
  }

  const totalPrice = price * amount;
  let discountAmount = 0;

  if (discount.type === 'percentage') {
    discountAmount = totalPrice * (discount.value / 100);
  } else {
    // Fixed amount - ensure it doesn't exceed the total price
    discountAmount = Math.min(discount.value * amount, totalPrice);
  }

  const finalPrice = Math.max(0, (totalPrice - discountAmount) / amount);

  return {
    finalPrice: parseFloat(finalPrice.toFixed(2)),
    discountAmount: parseFloat(discountAmount.toFixed(2)),
    originalPrice: price
  };
}

export default {
  namespaced: true,

  state: {
    calculatedLines: {
      firstInvoice: [],
      followUpInvoice: []
    },
    totals: {
      firstInvoice: 0,
      followUpInvoice: 0
    }
  },

  mutations: {
    SET_CALCULATED_LINES(state, { type, lines }) {
      state.calculatedLines[type] = lines;
    },
    SET_TOTALS(state, { type, total }) {
      state.totals[type] = total;
    }
  },

  actions: {
    calculateInvoice({ commit }, { contract, boxes, products }) {
      const currentPeriodLines = [];
      const startDate = moment(contract.start_date);
      const normalizedStart = moment(startDate.format('YYYY-MM-DD'), 'YYYY-MM-DD');
      const isAfter15 = startDate.date() >= 15;
      const isFirstDay = startDate.date() === 1;
      const frequency = contract.invoice_freq || 'month';
      const isWeekly = typeof frequency === 'string' && (frequency === 'week' || frequency.indexOf('week_') === 0);
      const weeksPerInvoice = isWeekly ? (parseInt(String(frequency).split('_')[1]) || 1) : 0;
      const isBusinessCustomer = contract.customer.type === 'business';
      const priceCalcMode = contract.price_calc_mode || null; // 'per_day' | 'fixed_price'
      const isFirstInvoice = !contract.is_follow_up;
      const daysInMonth = startDate.daysInMonth();
      const remainingDays = startDate.clone().endOf('month').diff(startDate, 'days') + 1;

      console.log('Debug - Invoice Calculation Input:', {
        isFirstInvoice,
        isAfter15,
        frequency,
        remainingDays,
        daysInMonth,
        boxes: boxes?.length,
        products: products?.length,
        isBusinessCustomer,
        startDate: startDate.format('YYYY-MM-DD')
      });

      if (isFirstInvoice) {
        // 1. Add box deposits first
        if (boxes?.length) {
          boxes.forEach(box => {
            if (box.deposit > 0) {
              currentPeriodLines.push({
                name: `Borg Box ${box.number}`,
                price: box.deposit,
                btw: 0,
                btwAmount: 0,
                amount: 1,
                total: parseFloat(box.deposit)
              });
            }

            const basePrice = parseFloat(box.price);

            if (isWeekly) {
              // Weekly: contiguous 7*n days from start date
              const periodStart = normalizedStart.clone();
              const periodEnd = normalizedStart.clone().add((weeksPerInvoice * 7) - 1, 'days');

              let weeklyPrice = 0;
              if (priceCalcMode === 'fixed_price') {
                weeklyPrice = parseFloat((basePrice).toFixed(2));
              } else {
                // per_day: sum per day using that day's month denominator (robust to boundary issues)
                let cursor = periodStart.clone();
                while (cursor.isSameOrBefore(periodEnd, 'day')) {
                  const denom = cursor.daysInMonth();
                  weeklyPrice += (basePrice / denom);
                  cursor = cursor.add(1, 'day');
                }
                weeklyPrice = parseFloat(weeklyPrice.toFixed(2));
              }
              const weeklyBTW = isBusinessCustomer ? parseFloat((weeklyPrice * (box.btw / 100)).toFixed(2)) : 0;

              currentPeriodLines.push({
                name: `Box ${box.number}`,
                price: weeklyPrice,
                btw: box.btw,
                btwAmount: weeklyBTW,
                amount: 1,
                total: parseFloat((isBusinessCustomer ? weeklyPrice + weeklyBTW : weeklyPrice).toFixed(2)),
                productId: box.id,
                productType: 'boxes',
                discountInfo: box.discountInfo
              });
            } else {
              // Monthly/Quarterly/Halfyear/Year
              if (isFirstDay) {
                // Start on the 1st: treat as full months (no day-split)
                const monthsForFreq = (frequency === 'quarter' ? 3 : frequency === 'halfyear' ? 6 : frequency === 'year' ? 12 : 1);
                const monthsPrice = basePrice;
                const monthsBTW = isBusinessCustomer ? parseFloat((monthsPrice * (box.btw / 100)).toFixed(2)) : 0;
                const periodText = monthsForFreq === 1 ? 'maand' : 'maanden';

                currentPeriodLines.push({
                  name: `Box ${box.number} (${monthsForFreq} ${periodText})`,
                  price: monthsPrice,
                  btw: box.btw,
                  btwAmount: monthsBTW,
                  amount: monthsForFreq,
                  total: parseFloat((isBusinessCustomer ? monthsPrice + monthsBTW : monthsPrice).toFixed(2)) * monthsForFreq,
                  productId: box.id,
                  productType: 'boxes',
                  discountInfo: box.discountInfo
                });
              } else {
                // remaining days + additional months per existing rules
                const daysPrice = parseFloat(((basePrice / daysInMonth) * remainingDays).toFixed(2));
                const daysBTW = isBusinessCustomer ? parseFloat((daysPrice * (box.btw / 100)).toFixed(2)) : 0;

                // First add the remaining days
                // For after15, discount should NOT be applied to remaining days - only to full month
                currentPeriodLines.push({
                  name: `Box ${box.number} (${remainingDays} ${remainingDays === 1 ? 'dag' : 'dagen'})`,
                  price: daysPrice,
                  btw: box.btw,
                  btwAmount: daysBTW,
                  amount: 1,
                  total: parseFloat((isBusinessCustomer ? daysPrice + daysBTW : daysPrice).toFixed(2)),
                  productId: box.id,
                  productType: 'boxes',
                  discountInfo: isAfter15 ? null : box.discountInfo
                });

                // Then add additional months if needed
                if (!isAfter15 && (frequency === 'quarter' || frequency === 'halfyear' || frequency === 'year')) {
                  const additionalMonths = frequency === 'quarter' ? 2 : frequency === 'halfyear' ? 5 : 11;
                  const monthsPrice = basePrice;
                  const monthsBTW = isBusinessCustomer ? parseFloat((monthsPrice * (box.btw / 100)).toFixed(2)) : 0;
                  const periodText = additionalMonths === 1 ? 'maand' : 'maanden';

                currentPeriodLines.push({
                  name: `Box ${box.number} (${additionalMonths} ${periodText})`,
                  price: monthsPrice,
                  btw: box.btw,
                  btwAmount: monthsBTW,
                  amount: additionalMonths,
                  total: parseFloat((isBusinessCustomer ? monthsPrice + monthsBTW : monthsPrice).toFixed(2)) * additionalMonths,
                  productId: box.id,
                  productType: 'boxes',
                  discountInfo: box.discountInfo
                })
                } else if (isAfter15) {
                  const additionalMonths = frequency === 'quarter' ? 3 : frequency === 'halfyear' ? 6 : frequency === 'year' ? 12 : 1;
                  const monthsPrice = basePrice;
                  const monthsBTW = isBusinessCustomer ? parseFloat((monthsPrice * (box.btw / 100)).toFixed(2)) : 0;
                  const periodText = additionalMonths === 1 ? 'maand' : 'maanden';

                  currentPeriodLines.push({
                    name: `Box ${box.number} (${additionalMonths} ${periodText})`,
                    price: monthsPrice,
                    btw: box.btw,
                    btwAmount: monthsBTW,
                    amount: additionalMonths,
                    total: parseFloat((isBusinessCustomer ? monthsPrice + monthsBTW : monthsPrice).toFixed(2)) * additionalMonths,
                    productId: box.id,
                    productType: 'boxes',
                    discountInfo: box.discountInfo
                  });
                }
              }
            }
          });
        }

        // 2. Add product deposits and one-time products
        if (products?.length) {
          products.forEach(product => {
            // Add deposit if any
            if (product.deposit > 0) {
              currentPeriodLines.push({
                name: `Borg ${product.name}`,
                price: product.deposit,
                btw: 0,
                btwAmount: 0,
                amount: product.amount || 1,
                total: parseFloat(((product.deposit) * (product.amount || 1)).toFixed(2))
              });
            }

            // For one-time products, add full price
            if (product.once) {
              const basePrice = parseFloat(product.price);
              const productBTW = isBusinessCustomer ? parseFloat((basePrice * (product.btw / 100)).toFixed(2)) : 0;
              const productAmount = product.amount || 1;

              currentPeriodLines.push({
                name: `${product.name} (eenmalig)`,
                price: basePrice,
                btw: product.btw,
                btwAmount: productBTW,
                amount: productAmount,
                total: parseFloat(((isBusinessCustomer ? basePrice + productBTW : basePrice) * productAmount).toFixed(2)),
                productId: product.id,
                productType: 'products',
                discountInfo: product.discountInfo
              });
            } else {
              const basePrice = parseFloat(product.price);
              const productAmount = product.amount || 1;

              if (isWeekly) {
                // Weekly: contiguous 7*n days from start date
                const periodStart = normalizedStart.clone();
                const periodEnd = normalizedStart.clone().add((weeksPerInvoice * 7) - 1, 'days');

                let weeklyPrice = 0;
                if (priceCalcMode === 'fixed_price') {
                  weeklyPrice = parseFloat((basePrice).toFixed(2));
                } else {
                  let cursor = periodStart.clone();
                  while (cursor.isSameOrBefore(periodEnd, 'day')) {
                    const denom = cursor.daysInMonth();
                    weeklyPrice += (basePrice / denom);
                    cursor = cursor.add(1, 'day');
                  }
                  weeklyPrice = parseFloat(weeklyPrice.toFixed(2));
                }
                const weeklyBTW = isBusinessCustomer ? parseFloat((weeklyPrice * (product.btw / 100)).toFixed(2)) : 0;

                currentPeriodLines.push({
                  name: `${product.name}`,
                  price: weeklyPrice,
                  btw: product.btw,
                  btwAmount: weeklyBTW,
                  amount: productAmount,
                  total: parseFloat(((isBusinessCustomer ? weeklyPrice + weeklyBTW : weeklyPrice) * productAmount).toFixed(2)),
                  productId: product.id,
                  productType: 'products',
                  discountInfo: product.discountInfo
                });
              } else {
                // Non-weekly products
                if (isFirstDay) {
                  const monthsForFreq = (frequency === 'quarter' ? 3 : frequency === 'halfyear' ? 6 : frequency === 'year' ? 12 : 1);
                  const monthsPrice = basePrice;
                  const monthsBTW = isBusinessCustomer ? parseFloat((monthsPrice * (product.btw / 100)).toFixed(2)) : 0;
                  const periodText = monthsForFreq === 1 ? 'maand' : 'maanden';

                  currentPeriodLines.push({
                    name: `${product.name} (${monthsForFreq} ${periodText})`,
                    price: monthsPrice,
                    btw: product.btw,
                    btwAmount: monthsBTW,
                    amount: monthsForFreq * productAmount,
                    total: parseFloat(((isBusinessCustomer ? monthsPrice + monthsBTW : monthsPrice) * productAmount * monthsForFreq).toFixed(2)),
                    productId: product.id,
                    productType: 'products',
                    discountInfo: product.discountInfo
                  });
                } else {
                  // For regular products, calculate remaining days then period clones
                  const daysPrice = parseFloat(((basePrice / daysInMonth) * remainingDays).toFixed(2));
                  const daysBTW = isBusinessCustomer ? parseFloat((daysPrice * (product.btw / 100)).toFixed(2)) : 0;

                  // First add the remaining days
                  // For after15, discount should NOT be applied to remaining days - only to full month
                  currentPeriodLines.push({
                    name: `${product.name} (${remainingDays} ${remainingDays === 1 ? 'dag' : 'dagen'})`,
                    price: daysPrice,
                    btw: product.btw,
                    btwAmount: daysBTW,
                    amount: productAmount,
                    total: parseFloat(((isBusinessCustomer ? daysPrice + daysBTW : daysPrice) * productAmount).toFixed(2)),
                    productId: product.id,
                    productType: 'products',
                    discountInfo: isAfter15 ? null : product.discountInfo  // No discount on partial days for after15
                  });

                  // Then add additional months if needed
                  if (!isAfter15 && (frequency === 'quarter' || frequency === 'halfyear' || frequency === 'year')) {
                    const additionalMonths = frequency === 'quarter' ? 2 : frequency === 'halfyear' ? 5 : 11;
                    const monthsPrice = basePrice;
                    const monthsBTW = isBusinessCustomer ? parseFloat((monthsPrice * (product.btw / 100)).toFixed(2)) : 0;
                    const periodText = additionalMonths === 1 ? 'maand' : 'maanden';

                  currentPeriodLines.push({
                    name: `${product.name} (${additionalMonths} ${periodText})`,
                    price: monthsPrice,
                    btw: product.btw,
                    btwAmount: monthsBTW,
                    amount: additionalMonths * productAmount,
                    total: parseFloat(((isBusinessCustomer ? monthsPrice + monthsBTW : monthsPrice) * productAmount * additionalMonths).toFixed(2)),
                    productId: product.id,
                    productType: 'products',
                    discountInfo: product.discountInfo
                  });
                  } else if (isAfter15) {
                  const additionalMonths = frequency === 'quarter' ? 3 : frequency === 'halfyear' ? 6 : frequency === 'year' ? 12 : 1;
                  const monthsPrice = basePrice;
                  const monthsBTW = isBusinessCustomer ? parseFloat((monthsPrice * (product.btw / 100)).toFixed(2)) : 0;
                  const periodText = additionalMonths === 1 ? 'maand' : 'maanden';

                  // For after15, discount SHOULD be applied to the full month product
                  currentPeriodLines.push({
                    name: `${product.name} (${additionalMonths} ${periodText})`,
                    price: monthsPrice,
                    btw: product.btw,
                    btwAmount: monthsBTW,
                    amount: additionalMonths * productAmount,
                    total: parseFloat(((isBusinessCustomer ? monthsPrice + monthsBTW : monthsPrice) * productAmount * additionalMonths).toFixed(2)),
                    productId: product.id,
                    productType: 'products',
                    discountInfo: product.discountInfo  // Apply discount to full month product
                  });
                  }
                }
              }
            }
          });
        }
      } else {
        // Follow-up invoice calculations
        const periodMonths = isWeekly ? 0 : (frequency === 'quarter' ? 3 : frequency === 'halfyear' ? 6 : frequency === 'year' ? 12 : 1);

        // Add boxes for the full period
        if (boxes?.length) {
          boxes.forEach(box => {
            const basePrice = parseFloat(box.price);

            if (isWeekly) {
              // Follow-up: start day after first period end
              const periodStart = normalizedStart.clone().add(weeksPerInvoice * 7, 'days');
              const periodEnd = periodStart.clone().add((weeksPerInvoice * 7) - 1, 'days');

              let nextDaysPrice = 0;
              if (priceCalcMode === 'fixed_price') {
                nextDaysPrice = parseFloat((basePrice).toFixed(2));
              } else {
                let cursor = periodStart.clone();
                while (cursor.isSameOrBefore(periodEnd, 'day')) {
                  const denom = cursor.daysInMonth();
                  nextDaysPrice += (basePrice / denom);
                  cursor = cursor.add(1, 'day');
                }
                nextDaysPrice = parseFloat(nextDaysPrice.toFixed(2));
              }
              const nextDaysBTW = isBusinessCustomer ? parseFloat((nextDaysPrice * (box.btw / 100)).toFixed(2)) : 0;

              currentPeriodLines.push({
                name: `Box ${box.number}`,
                price: nextDaysPrice,
                btw: box.btw,
                btwAmount: nextDaysBTW,
                amount: 1,
                total: parseFloat((isBusinessCustomer ? nextDaysPrice + nextDaysBTW : nextDaysPrice).toFixed(2)),
                productId: box.id,
                productType: 'boxes',
                discountInfo: box.discountInfo
              });
            } else {
              const periodPrice = parseFloat((basePrice * periodMonths).toFixed(2));
              const periodBTW = isBusinessCustomer ? parseFloat((periodPrice * (box.btw / 100)).toFixed(2)) : 0;

              currentPeriodLines.push({
                name: `Box ${box.number} (${periodMonths} ${periodMonths === 1 ? 'maand' : 'maanden'})`,
                price: basePrice,
                btw: box.btw,
                btwAmount: periodBTW,
                amount: periodMonths,
                total: isBusinessCustomer ? periodPrice + periodBTW : periodPrice,
                productId: box.id,
                productType: 'boxes',
                discountInfo: box.discountInfo
              });
            }
          });
        }

        // Add regular (non-one-time) products for the full period
        if (products?.length) {
          products.filter(p => !p.once).forEach(product => {
            const basePrice = parseFloat(product.price);

            if (isWeekly) {
              // Follow-up: start day after first period end
              const periodStart = normalizedStart.clone().add(weeksPerInvoice * 7, 'days');
              const periodEnd = periodStart.clone().add((weeksPerInvoice * 7) - 1, 'days');

              let nextDaysPrice = 0;
              if (priceCalcMode === 'fixed_price') {
                nextDaysPrice = parseFloat((basePrice).toFixed(2));
              } else {
                let cursor = periodStart.clone();
                while (cursor.isSameOrBefore(periodEnd, 'day')) {
                  const denom = cursor.daysInMonth();
                  nextDaysPrice += (basePrice / denom);
                  cursor = cursor.add(1, 'day');
                }
                nextDaysPrice = parseFloat(nextDaysPrice.toFixed(2));
              }
              const nextDaysBTW = isBusinessCustomer ? parseFloat((nextDaysPrice * (product.btw / 100)).toFixed(2)) : 0;

              currentPeriodLines.push({
                name: `${product.name}`,
                price: nextDaysPrice,
                btw: product.btw,
                btwAmount: nextDaysBTW,
                amount: (product.amount || 1),
                total: parseFloat(((isBusinessCustomer ? nextDaysPrice + nextDaysBTW : nextDaysPrice) * (product.amount || 1)).toFixed(2)),
                productId: product.id,
                productType: 'products',
                discountInfo: product.discountInfo
              });
            } else {
              const periodPrice = parseFloat((basePrice * periodMonths).toFixed(2));
              const periodBTW = isBusinessCustomer ? parseFloat((periodPrice * (product.btw / 100)).toFixed(2)) : 0;

              currentPeriodLines.push({
                name: `${product.name} (${periodMonths} ${periodMonths === 1 ? 'maand' : 'maanden'})`,
                price: basePrice,
                btw: product.btw,
                btwAmount: periodBTW,
                amount: periodMonths * (product.amount || 1),
                total: parseFloat(((isBusinessCustomer ? periodPrice + periodBTW : periodPrice) * (product.amount || 1)).toFixed(2)),
                productId: product.id,
                productType: 'products',
                discountInfo: product.discountInfo
              });
            }
          });
        }
      }

      // Apply discounts and add discount lines
      const invoiceNumber = contract.is_follow_up ? 2 : 1;
      const linesWithDiscounts = [];

      currentPeriodLines.forEach(line => {
        // Add the original line
        linesWithDiscounts.push(line);

        // Check if this line has discount info
        if (line.discountInfo && line.productId) {
          const discount = getApplicableDiscount(line.discountInfo, invoiceNumber);
          if (discount && discount.value > 0) {
            const discountResult = applyDiscount(line.price, line.amount, discount);

            if (discountResult.discountAmount > 0) {
              // Add discount line
              linesWithDiscounts.push({
                name: `  Korting: ${discount.type === 'percentage' ? discount.value + '%' : '€' + discount.value}${discount.description ? ' (' + discount.description + ')' : ''}`,
                price: 0,
                btw: 0,
                btwAmount: 0,
                amount: 1,
                total: parseFloat((-discountResult.discountAmount).toFixed(2)),
                isDiscountLine: true
              });
            }
          }
        }
      });

      console.log(`🔥 Final invoice lines with discounts (${contract.is_follow_up ? 'follow-up' : 'first'})`, linesWithDiscounts);

      // Calculate totals
      const totals = {
        firstInvoice: linesWithDiscounts.reduce((sum, line) => {
          const lineTotal = parseFloat(line?.total || 0);
          return parseFloat((sum + lineTotal).toFixed(2));
        }, 0),
        followUpInvoice: 0
      };

      // Update state based on invoice type
      if (contract.is_follow_up) {
        commit('SET_CALCULATED_LINES', {
          type: 'followUpInvoice',
          lines: linesWithDiscounts
        });
        commit('SET_TOTALS', {
          type: 'followUpInvoice',
          total: totals.firstInvoice
        });
      } else {
        commit('SET_CALCULATED_LINES', {
          type: 'firstInvoice',
          lines: linesWithDiscounts
        });
        commit('SET_TOTALS', {
          type: 'firstInvoice',
          total: totals.firstInvoice
        });
      }

      return linesWithDiscounts;
    }
  },

  getters: {
    grandTotal: state => {
      return parseFloat((state.totals.firstInvoice + state.totals.followUpInvoice).toFixed(2));
    }
  }
};
